<template>
  <div class="tw:p-4">
    <div class="tw:flex tw:items-center tw:justify-between">
      <div class="tw:mb-2 tw:font-bold">
        {{ getLocaleText("stream_filters_languages.language") }}
      </div>
      <div>
        <a
          v-if="languageCodes.length || excludedLanguageCodes.length"
          class="tw:cursor-pointer tw:text-denim-400 tw:hover:text-denim-700"
          data-testid="clear-all"
          @click="clearAll"
        >
          {{ getLocaleText("stream_filters_content.clear_all") }}
        </a>
      </div>
    </div>
    <div class="tw:mb-2 tw:flex">
      <Tabs
        v-model="languageTab"
        class="tw:font-bold"
        data-testid="tabs"
        type="toggles"
      >
        <Tab
          data-testid="include-tab"
          :label="getLocaleText('stream_filters_content.include')"
          name="include"
          new-design
          type="toggle"
        />
        <Tab
          data-testid="exclude-tab"
          :label="getLocaleText('stream_filters_content.exclude')"
          name="exclude"
          new-design
          type="toggle"
        />
      </Tabs>
    </div>
    <div class="tw:mb-1 tw:text-xs tw:text-denim-700">
      {{ notAvailable }}
    </div>
    <div class="tw:flex tw:flex-col tw:gap-2">
      <InputSelect
        v-model="localLanguageCodes"
        clearable
        emit-value
        multiple
        new-design
        option-label="name"
        option-prop="value"
        :options="filteredLanguageOptions"
        :placeholder="getLocaleText('global.search')"
        use-input
        @filter="onFilterLanguage"
      />
      <div
        v-if="selectedLanguages.length"
        class="tw:flex tw:flex-wrap tw:gap-1"
        data-testid="tags"
      >
        <Tag
          v-for="(selectedLanguage, selectedLanguageIndex) in selectedLanguages"
          :key="selectedLanguageIndex"
          color="secondary"
          :data-testid="`tag-${selectedLanguage.value}`"
          hide-cursor
          :label="selectedLanguage.name as string"
          rounded
          @remove="removeLanguage(selectedLanguage)"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";

import { getLocaleText } from "shared/boot/i18n";
import { InputSelect, Tab, Tabs, Tag } from "shared/components/base";
import { languageCodes as allowedLanguageCodes } from "shared/constants/languages";

interface StreamFiltersLanguagesOption {
  value: string;
  name: string;
}

const languageCodes = defineModel<string[]>("languageCodes", {
  default: () => [],
});

const excludedLanguageCodes = defineModel<string[]>("excludedLanguageCodes", {
  default: () => [],
});

const languageFilter = ref("");

const languageTab = ref(
  excludedLanguageCodes.value.length ? "exclude" : "include"
);

const notAvailable = computed(() => {
  if (languageTab.value === "include") {
    return getLocaleText("stream_filters_content.excluded_not_available");
  }

  return getLocaleText("stream_filters_content.included_not_available");
});

const localLanguageCodes = computed<string[]>({
  get() {
    return languageTab.value === "exclude"
      ? excludedLanguageCodes.value
      : languageCodes.value;
  },
  set(value) {
    if (languageTab.value === "exclude") {
      excludedLanguageCodes.value = value;
    } else {
      languageCodes.value = value;
    }
  },
});

const languageOptions = computed<StreamFiltersLanguagesOption[]>(() =>
  allowedLanguageCodes.map((code) => ({
    value: code,
    name: getLocaleText(`constants.languages.${code}`),
  }))
);

const filteredLanguageOptions = computed<StreamFiltersLanguagesOption[]>(() =>
  languageOptions.value.filter((language) => {
    if (localLanguageCodes.value.includes(language.value)) {
      return false;
    }

    if (languageFilter.value) {
      return language.name
        .toLowerCase()
        .includes(languageFilter.value.toLowerCase());
    }

    return true;
  })
);

const selectedLanguages = computed<StreamFiltersLanguagesOption[]>(() =>
  languageOptions.value.filter((languageOption) =>
    localLanguageCodes.value.includes(languageOption.value)
  )
);

function onFilterLanguage(value: string) {
  languageFilter.value = value;
}

function removeLanguage(language: StreamFiltersLanguagesOption) {
  localLanguageCodes.value = localLanguageCodes.value.filter(
    (languageKey) => languageKey !== language.value
  );
}

function clearAll() {
  languageCodes.value = [];
  excludedLanguageCodes.value = [];
}

watch(languageTab, () => {
  clearAll();
});
</script>
